import {
  ArrowBack, Notifications, Search, NavigateNext, NavigateBefore,
  ErrorOutline, CheckCircle
} from "@material-ui/icons";
import { useEffect, useState, useCallback, useMemo } from "react";
import { useHistory } from "react-router-dom";
import styled from "styled-components";
import Tooltip from "@material-ui/core/Tooltip";

import fetchClient from "../../../api/fetchClient";
import Loader from "../../../components/Loader";
import message from "../../../helper/message";
import {
  Container,
  ModernHeader,
  HeaderLeft,
  HeaderContent,
  PageTitle,
  PageSubtitle,
  HeaderActions,
  BackButton,
  ResultsCard,
  SectionInfoTitle,
  SectionSubtitle,
  TableWrapper,
  StreamsTable,
  TableHead,
  TableRow,
  TableCell,
  EmptyState,
  EmptyTitle,
  EmptyText,
  getTheme,
} from "../../../components/common";

// ── Helpers ──────────────────────────────────────────────────────
const pad = (n) => String(n).padStart(2, "0");
const toLocalDT = (d) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;

const today = new Date();
const DEFAULT_FROM = toLocalDT(new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0));
const DEFAULT_TO   = toLocalDT(new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59));

const formatDate = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("pt-BR", {
    day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
};

// ── Component ─────────────────────────────────────────────────────
function NotificacoesWms() {
  const history = useHistory();
  const theme = getTheme("senior");

  // Empresas para o select
  const [empresas, setEmpresas] = useState([]);

  // Server-side filters
  const [companyId, setCompanyId] = useState("");
  const [notified, setNotified]   = useState("N");
  const [from, setFrom]           = useState(DEFAULT_FROM);
  const [to, setTo]               = useState(DEFAULT_TO);

  // Data
  const [items, setItems]         = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);

  // Cursor pagination
  const [nextKey, setNextKey]         = useState(null);
  const [historyKeys, setHistoryKeys] = useState([]);
  const pageSize = 50;

  // Client-side search + pagination
  const [searchTerm, setSearchTerm]       = useState("");
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const [currentPage, setCurrentPage]     = useState(1);

  // ── Fetch ────────────────────────────────────────────────────────
  const fetchData = useCallback(async ({ nextKeyParam = null } = {}) => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (companyId.trim()) params.set("companyId", companyId.trim());
      if (notified)         params.set("notified", notified);
      if (from)             params.set("startDate", new Date(from).toISOString());
      if (to)               params.set("endDate",   new Date(to).toISOString());
      params.set("pageSize", pageSize);
      if (nextKeyParam)     params.set("nextKey", nextKeyParam);

      const { data } = await fetchClient.get(`/wms/notifications?${params.toString()}`);
      setItems(data.items || []);
      setTotalCount(data.count ?? (data.items?.length ?? 0));
      setNextKey(data.nextKey || null);
      setSearchTerm("");
      setCurrentPage(1);
    } catch (err) {
      message.error(err.response?.data?.message || "Erro ao buscar notificações.");
    } finally {
      setIsLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, notified, from, to]);

  // Carrega lista de empresas para o select
  useEffect(() => {
    fetchClient.get("/wms/getAll")
      .then(({ data }) => setEmpresas(data || []))
      .catch(() => {});
  }, []);

  useEffect(() => { fetchData(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = () => {
    setHistoryKeys([]);
    setNextKey(null);
    fetchData();
  };

  const goNext = () => {
    if (!nextKey) return;
    setHistoryKeys((prev) => [...prev, nextKey]);
    fetchData({ nextKeyParam: nextKey });
  };

  const goPrev = () => {
    const prev = historyKeys[historyKeys.length - 2] || null;
    setHistoryKeys((h) => h.slice(0, -1));
    fetchData({ nextKeyParam: prev });
  };

  const hasPrev = historyKeys.length > 0;

  // ── Client-side filter + pagination ──────────────────────────────
  const filtered = useMemo(() => {
    if (!searchTerm) return items;
    const t = searchTerm.toLowerCase();
    return items.filter((i) =>
      [i.companyName, i.companyId, i.processType, i.visualKey, i.objectKey, i.latestErrorMessage]
        .some((v) => v && v.toLowerCase().includes(t))
    );
  }, [items, searchTerm]);

  const paginated = useMemo(() => {
    const start = (currentPage - 1) * recordsPerPage;
    return filtered.slice(start, start + recordsPerPage);
  }, [filtered, currentPage, recordsPerPage]);

  const totalPages = Math.ceil(filtered.length / recordsPerPage);

  useEffect(() => { setCurrentPage(1); }, [searchTerm, recordsPerPage]);

  // ── Render ────────────────────────────────────────────────────────
  if (isLoading) return <Loader />;

  return (
    <Container>
      <ModernHeader>
        <HeaderLeft>
          <BackButton onClick={() => history.goBack()}>
            <ArrowBack />
          </BackButton>
          <HeaderContent>
            <PageTitle>
              <Notifications style={{ marginRight: "12px", fontSize: "32px" }} />
              Notificações WMS
            </PageTitle>
            <PageSubtitle>Histórico de notificações de erros de integração</PageSubtitle>
          </HeaderContent>
        </HeaderLeft>
        <HeaderActions />
      </ModernHeader>

      {/* Filtros */}
      <FilterCard>
        <FilterGrid>
          <FilterField flex="2" minWidth="200px" maxWidth="320px">
            <FilterLabel>Empresa</FilterLabel>
            <FilterSelect value={companyId} onChange={(e) => setCompanyId(e.target.value)}>
              <option value="">Todas as empresas</option>
              {empresas.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.name || e.id}
                </option>
              ))}
            </FilterSelect>
          </FilterField>
          <FilterField flex="0 0 150px" minWidth="150px" maxWidth="150px">
            <FilterLabel>Notificado</FilterLabel>
            <FilterSelect value={notified} onChange={(e) => setNotified(e.target.value)}>
              <option value="">Todos</option>
              <option value="N">Pendente</option>
              <option value="Y">Notificado</option>
            </FilterSelect>
          </FilterField>
          <FilterField flex="1" minWidth="180px" maxWidth="220px">
            <FilterLabel>De</FilterLabel>
            <FilterInput type="datetime-local" value={from} onChange={(e) => setFrom(e.target.value)} />
          </FilterField>
          <FilterField flex="1" minWidth="180px" maxWidth="220px">
            <FilterLabel>Até</FilterLabel>
            <FilterInput type="datetime-local" value={to} onChange={(e) => setTo(e.target.value)} />
          </FilterField>
          <FilterField flex="0 0 auto" minWidth="auto" style={{ justifyContent: "flex-end", alignItems: "flex-end" }}>
            <SearchBtn gradient={theme.gradient} onClick={handleSearch}>
              <Search style={{ fontSize: 18, marginRight: 6 }} />
              Buscar
            </SearchBtn>
          </FilterField>
        </FilterGrid>
      </FilterCard>

      {items.length === 0 ? (
        <ResultsCard>
          <EmptyState>
            <Notifications style={{ fontSize: 48, color: "#cbd5e1", marginBottom: 16 }} />
            <EmptyTitle>Nenhuma notificação encontrada</EmptyTitle>
            <EmptyText>Ajuste os filtros e clique em Buscar.</EmptyText>
          </EmptyState>
        </ResultsCard>
      ) : (
        <ResultsCard>
          <SectionInfoWrapper>
            <SectionInfoLeft>
              <SectionInfoTitle>Notificações registradas</SectionInfoTitle>
              <SectionSubtitle>
                Mostrando {paginated.length} de {filtered.length} {filtered.length === 1 ? "notificação" : "notificações"}
                {searchTerm && ` (filtrado de ${totalCount})`}
              </SectionSubtitle>
            </SectionInfoLeft>
            <SearchContainer>
              <SearchIconWrapper>
                <Search style={{ fontSize: 20 }} />
              </SearchIconWrapper>
              <SearchInput
                type="text"
                placeholder="Pesquisar por empresa, processo, documento..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <ClearSearchButton onClick={() => setSearchTerm("")}>×</ClearSearchButton>
              )}
            </SearchContainer>
          </SectionInfoWrapper>

          <TableWrapper>
            <StreamsTable>
              <thead>
                <tr>
                  <TableHead>Data/Hora</TableHead>
                  <TableHead>Empresa</TableHead>
                  <TableHead>Tipo de Processo</TableHead>
                  <TableHead>Documento</TableHead>
                  <TableHead>Qtd</TableHead>
                  <TableHead>Último Erro</TableHead>
                  <TableHead>Notificado</TableHead>
                </tr>
              </thead>
              <tbody>
                {paginated.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} style={{ textAlign: "center", color: "#6b7280" }}>
                      Nenhum resultado encontrado para os filtros aplicados
                    </TableCell>
                  </TableRow>
                ) : (
                  paginated.map((item) => (
                    <TableRow key={`${item.pk}#${item.sk}`}>
                      <TableCell>
                        <DateText>{formatDate(item.createdAt)}</DateText>
                      </TableCell>
                      <TableCell>
                        <CompanyCell>
                          <CompanyName>{item.companyName || item.companyId}</CompanyName>
                          <CompanyId>{item.companyId}</CompanyId>
                        </CompanyCell>
                      </TableCell>
                      <TableCell>
                        <TypeBadge>{item.processType || "—"}</TypeBadge>
                      </TableCell>
                      <TableCell>{item.visualKey || item.objectKey || "—"}</TableCell>
                      <TableCell>
                        {item.errorCount > 0
                          ? <ErrorCountBadge>{item.errorCount}</ErrorCountBadge>
                          : "—"}
                      </TableCell>
                      <TableCell>
                        <Tooltip title={item.latestErrorMessage || ""} placement="top">
                          <ErrorText>{item.latestErrorMessage || "—"}</ErrorText>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <NotifiedBadge notified={item.notified}>
                          {item.notified === "Y"
                            ? <><CheckCircle style={{ fontSize: 14, marginRight: 4 }} />Notificado</>
                            : <><ErrorOutline style={{ fontSize: 14, marginRight: 4 }} />Pendente</>
                          }
                        </NotifiedBadge>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </tbody>
            </StreamsTable>
          </TableWrapper>

          <PaginationWrapper>
            <RecordsPerPageContainer>
              <RecordsPerPageLabel>Registros por página:</RecordsPerPageLabel>
              <RecordsPerPageSelect
                value={recordsPerPage}
                onChange={(e) => setRecordsPerPage(Number(e.target.value))}
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </RecordsPerPageSelect>
            </RecordsPerPageContainer>

            <PaginationContainer>
              <PaginationButton disabled={!hasPrev && currentPage === 1} onClick={() => {
                if (currentPage > 1) setCurrentPage((p) => p - 1);
                else goPrev();
              }}>
                <NavigateBefore />
              </PaginationButton>
              <PageIndicator>Página {currentPage} de {totalPages || 1}</PageIndicator>
              <PaginationButton disabled={currentPage >= totalPages && !nextKey} onClick={() => {
                if (currentPage < totalPages) setCurrentPage((p) => p + 1);
                else goNext();
              }}>
                <NavigateNext />
              </PaginationButton>
            </PaginationContainer>
          </PaginationWrapper>
        </ResultsCard>
      )}
    </Container>
  );
}

export default NotificacoesWms;


// ── Filter card ───────────────────────────────────────────────────
const FilterCard = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto 24px;
  padding: 20px 2rem;
  background: white;
  border: 1px solid #e8e8e8;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  box-sizing: border-box;
`;

const FilterGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: flex-end;
`;

const FilterField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: ${(p) => p.flex || "1"};
  min-width: ${(p) => p.minWidth || "160px"};
  max-width: ${(p) => p.maxWidth || "none"};
`;

const FilterLabel = styled.label`
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  font-family: 'Mulish', sans-serif;
`;

const FilterInput = styled.input`
  padding: 10px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  font-family: 'Mulish', sans-serif;
  outline: none;
  transition: border-color 0.2s;
  &:focus { border-color: #84cc16; box-shadow: 0 0 0 3px rgba(132, 204, 22, 0.1); }
`;

const FilterSelect = styled.select`
  padding: 10px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  font-family: 'Mulish', sans-serif;
  background: white;
  cursor: pointer;
  outline: none;
  transition: border-color 0.2s;
  &:focus { border-color: #84cc16; box-shadow: 0 0 0 3px rgba(132, 204, 22, 0.1); }
`;

const SearchBtn = styled.button`
  display: flex;
  align-items: center;
  padding: 10px 20px;
  border: none;
  border-radius: 8px;
  background: ${(p) => p.gradient || "linear-gradient(135deg, #84cc16, #65a30d)"};
  color: white;
  font-size: 14px;
  font-weight: 600;
  font-family: 'Mulish', sans-serif;
  cursor: pointer;
  white-space: nowrap;
  transition: opacity 0.2s;
  &:hover { opacity: 0.88; }
`;

// ── Header ────────────────────────────────────────────────────────
const RefreshButton = styled.button`
  background: linear-gradient(135deg, #84cc16 0%, #65a30d 100%);
  color: white;
  border: none;
  border-radius: 12px;
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  font-family: 'Mulish', sans-serif;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(132, 204, 22, 0.3);
  &:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 4px 12px rgba(132, 204, 22, 0.4); }
  &:disabled { background: #bdbdbd; cursor: not-allowed; box-shadow: none; opacity: 0.6; }
`;

// ── Section info + search ─────────────────────────────────────────
const SectionInfoWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
  padding: 24px 16px 0 16px;
  gap: 16px;
  @media (max-width: 768px) { flex-direction: column; }
`;

const SectionInfoLeft = styled.div`
  display: flex;
  flex-direction: column;
`;

const SearchContainer = styled.div`
  display: flex;
  align-items: center;
  position: relative;
  min-width: 300px;
  @media (max-width: 768px) { min-width: 100%; width: 100%; }
`;

const SearchIconWrapper = styled.div`
  position: absolute;
  left: 12px;
  color: #9ca3af;
  display: flex;
  align-items: center;
  pointer-events: none;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 10px 40px 10px 44px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  font-family: 'Mulish', sans-serif;
  transition: all 0.2s ease;
  &:focus { outline: none; border-color: #84cc16; box-shadow: 0 0 0 3px rgba(132, 204, 22, 0.1); }
  &::placeholder { color: #9ca3af; }
`;

const ClearSearchButton = styled.button`
  position: absolute;
  right: 8px;
  width: 24px;
  height: 24px;
  border: none;
  border-radius: 50%;
  background: #e5e7eb;
  color: #6b7280;
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  &:hover { background: #d1d5db; color: #374151; }
`;

// ── Pagination ────────────────────────────────────────────────────
const PaginationWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid #e5e7eb;
`;

const RecordsPerPageContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const RecordsPerPageLabel = styled.span`
  font-size: 14px;
  color: #6b7280;
  font-family: 'Mulish', sans-serif;
`;

const RecordsPerPageSelect = styled.select`
  padding: 8px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  font-family: 'Mulish', sans-serif;
  background: white;
  cursor: pointer;
  transition: all 0.2s ease;
  &:focus { outline: none; border-color: #84cc16; }
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
`;

const PaginationButton = styled.button`
  width: 40px;
  height: 40px;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  background: linear-gradient(135deg, #84cc16 0%, #65a30d 100%);
  color: #fff;
  font-size: 24px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(132, 204, 22, 0.3);
  &:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 4px 12px rgba(132, 204, 22, 0.4); }
  &:active:not(:disabled) { transform: translateY(0); }
  &:disabled { background: #bdbdbd; cursor: not-allowed; box-shadow: none; }
`;

const PageIndicator = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #2c3e50;
  font-family: 'Mulish', sans-serif;
  padding: 0 16px;
`;

// ── Table cells ───────────────────────────────────────────────────
const DateText = styled.span`
  font-size: 13px;
  color: #374151;
  font-family: 'Mulish', sans-serif;
`;

const TypeBadge = styled.span`
  display: inline-block;
  padding: 4px 12px;
  background: #f0fdf4;
  color: #15803d;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  font-family: 'Mulish', sans-serif;
`;

const ErrorText = styled.span`
  display: block;
  max-width: 280px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 12px;
  color: #dc2626;
  font-family: 'Mulish', sans-serif;
  cursor: default;
`;

const NotifiedBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  font-family: 'Mulish', sans-serif;
  background: ${(p) => p.notified === "Y" ? "#dcfce7" : "#fee2e2"};
  color:      ${(p) => p.notified === "Y" ? "#16a34a" : "#dc2626"};
`;

const CompanyCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const CompanyName = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #1f2937;
  font-family: 'Mulish', sans-serif;
`;

const CompanyId = styled.span`
  font-size: 11px;
  color: #9ca3af;
  font-family: 'Mulish', monospace;
`;

const ErrorCountBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  padding: 2px 8px;
  background: #fee2e2;
  color: #dc2626;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 700;
  font-family: 'Mulish', sans-serif;
`;
